// Ads results card: the machine-readable summary an ads run ends its reply with.
//
// The agent writes one fenced block, version 1:
//
//   ```ads-result
//   {"v":1,"client":"Cathy Nail House","review_url":"https://drive.reputifly.cloud/...",
//    "launch":[{"id":"O1","why":"..."}],
//    "ads":[{"id":"O1","title":"GEL MANI. $18.","score":82,"file":"O1-4x5.png",
//            "route":"code|hybrid|ai","status":"launch|reserve|dropped"}],
//    "client_must_confirm":["..."],"client_message":"Hi ...",
//    "helpers":{"finished":45,"failed":0},"notes":"..."}
//   ```
//
// extractAdsResult() finds that block, validates every field and returns the
// text around it plus a clean, bounded result for AdsResultCard. Anything that
// is not exactly this shape (bad JSON, a wrong type, an unknown enum value)
// returns null, and the reply renders as plain Markdown exactly as before, so
// nothing the agent wrote is ever hidden. Strings are capped and stripped of
// control and bidi-override characters, numbers must be finite, arrays are cut
// to 12 entries, and the result only ever reaches the page as React text.

export type AdRoute = "code" | "hybrid" | "ai";
export type AdStatus = "launch" | "reserve" | "dropped";

export interface AdsResultAd {
  id: string;
  title: string;
  /** 0–100. */
  score?: number;
  /** The image's file name, matched by basename to the same reply's attached files. */
  file?: string;
  route?: AdRoute;
  status?: AdStatus;
}

export interface AdsResult {
  client: string;
  /** Only a https://drive.reputifly.cloud/ address survives; anything else is dropped. */
  reviewUrl?: string;
  launch: { id: string; why: string }[];
  ads: AdsResultAd[];
  clientMustConfirm: string[];
  clientMessage?: string;
  helpers?: { finished: number; failed: number };
  notes?: string;
}

export interface AdsResultBlock {
  /** Reply text before the block, trailing blank space removed. */
  before: string;
  /** Reply text after the block, leading blank lines removed. */
  after: string;
  result: AdsResult;
}

export const ADS_RESULT_MAX_ITEMS = 12;
const MAX_BLOCK_CHARS = 64_000;
const MAX_COUNT = 100_000;
const REVIEW_HOST = "drive.reputifly.cloud";

// The opening fence must start a line; the block ends at the first bare ``` line.
const FENCE_RE = /^ {0,3}```ads-result[ \t]*\r?\n([\s\S]*?)\r?\n {0,3}```[ \t]*$/m;
// C0/C1 controls (tab and newline are handled separately) plus the direction
// marks, embeddings, overrides and isolates that can visually reorder text.
// eslint-disable-next-line no-control-regex -- matching control characters is the point
const HIDDEN_RE = /[\u0000-\u0008\u000B-\u001F\u007F-\u009F\u200E\u200F\u202A-\u202E\u2066-\u2069]/g;
const LOCAL_IMAGE_RE = /\.(png|jpe?g|gif|webp|heic|heif)$/i;
/** The prefix a chat adds to an attached file's name: "1790509918111-0-ushxq6-". */
const STORED_PREFIX = /^\d{10,16}-\d{1,4}-[a-z0-9]{3,12}-(?=[^.])/i;

const ROUTES: readonly AdRoute[] = ["code", "hybrid", "ai"];
const STATUSES: readonly AdStatus[] = ["launch", "reserve", "dropped"];

/** Thrown inside the parser for any shape mismatch; never escapes this module. */
class ShapeError extends Error {}
function reject(): never {
  throw new ShapeError("ads-result shape");
}

type JsonObject = Record<string, unknown>;
function isObject(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
/** Own properties only: a hostile "__proto__" or "constructor" key is just data. */
function field(source: JsonObject, key: string): unknown {
  return Object.prototype.hasOwnProperty.call(source, key) ? source[key] : undefined;
}
function absent(value: unknown): boolean {
  return value === undefined || value === null;
}

/** Cap by characters (not UTF-16 units) so an emoji is never cut in half. */
function cap(text: string, max: number): string {
  const chars = Array.from(text);
  return chars.length <= max ? text : chars.slice(0, max - 1).join("").trimEnd() + "…";
}

/** A string, cleaned and capped. Single-line text folds every run of whitespace to one space. */
function text(value: unknown, max: number, multiline = false): string {
  if (typeof value !== "string") reject();
  const visible = value.replace(/\r\n?/g, "\n").replace(HIDDEN_RE, "");
  const tidy = multiline
    ? visible.replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim()
    : visible.replace(/\s+/g, " ").trim();
  return cap(tidy, max);
}
function requiredText(value: unknown, max: number): string {
  const result = text(value, max);
  if (!result) reject();
  return result;
}
/** Missing, null or blank reads as "not given"; any other non-string is a shape error. */
function optionalText(value: unknown, max: number, multiline = false): string | undefined {
  return absent(value) ? undefined : text(value, max, multiline) || undefined;
}
function optionalEnum<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  if (absent(value)) return undefined;
  if (typeof value !== "string" || !allowed.includes(value as T)) reject();
  return value as T;
}
function list<T>(value: unknown, read: (item: unknown) => T | undefined): T[] {
  if (absent(value)) return [];
  if (!Array.isArray(value)) reject();
  const items: T[] = [];
  for (const item of value.slice(0, ADS_RESULT_MAX_ITEMS)) {
    const parsed = read(item);
    if (parsed !== undefined) items.push(parsed);
  }
  return items;
}
function count(value: unknown): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0 || value > MAX_COUNT) reject();
  return value;
}

/** The review page link, only when it is https on drive.reputifly.cloud with no credentials or port. */
export function safeReviewUrl(value: string): string | undefined {
  if (value.length > 2048) return undefined;
  try {
    const url = new URL(value.trim());
    return url.protocol === "https:" && url.host === REVIEW_HOST && !url.username && !url.password
      ? url.href : undefined;
  } catch {
    return undefined;
  }
}

function readAd(item: unknown): AdsResultAd {
  if (!isObject(item)) reject();
  const score = field(item, "score");
  if (!absent(score) && (typeof score !== "number" || !Number.isFinite(score) || score < 0 || score > 100)) reject();
  const ad: AdsResultAd = { id: requiredText(field(item, "id"), 24), title: text(field(item, "title"), 120) };
  if (typeof score === "number") ad.score = Math.round(score);
  const file = optionalText(field(item, "file"), 200);
  if (file) ad.file = file;
  const route = optionalEnum(field(item, "route"), ROUTES);
  if (route) ad.route = route;
  const status = optionalEnum(field(item, "status"), STATUSES);
  if (status) ad.status = status;
  return ad;
}

/** Validate a parsed ads-result value. Null unless it is exactly the version 1 shape. */
export function parseAdsResult(data: unknown): AdsResult | null {
  try {
    if (!isObject(data) || field(data, "v") !== 1) return null;
    const ads = list(field(data, "ads"), readAd);
    if (ads.length === 0) return null;
    const review = field(data, "review_url");
    if (!absent(review) && typeof review !== "string") return null;
    const result: AdsResult = {
      client: requiredText(field(data, "client"), 80),
      launch: list(field(data, "launch"), item => {
        if (!isObject(item)) reject();
        return { id: requiredText(field(item, "id"), 24), why: optionalText(field(item, "why"), 400) ?? "" };
      }),
      ads,
      clientMustConfirm: list(field(data, "client_must_confirm"), item => text(item, 240) || undefined),
    };
    const reviewUrl = typeof review === "string" ? safeReviewUrl(review) : undefined;
    if (reviewUrl) result.reviewUrl = reviewUrl;
    const message = optionalText(field(data, "client_message"), 3000, true);
    if (message) result.clientMessage = message;
    const notes = optionalText(field(data, "notes"), 800, true);
    if (notes) result.notes = notes;
    const helpers = field(data, "helpers");
    if (!absent(helpers)) {
      if (!isObject(helpers)) return null;
      result.helpers = { finished: count(field(helpers, "finished")), failed: count(field(helpers, "failed")) };
    }
    return result;
  } catch (error) {
    if (error instanceof ShapeError) return null;
    throw error;
  }
}

/**
 * Find the first ```ads-result block in a reply. Returns the text on either side
 * and the validated result, or null when there is no block or it is not valid
 * (the caller then renders the reply unchanged).
 */
export function extractAdsResult(reply: string): AdsResultBlock | null {
  if (!reply.includes("```ads-result")) return null;
  const match = FENCE_RE.exec(reply);
  if (!match || match[1]!.length > MAX_BLOCK_CHARS) return null;
  let data: unknown;
  try {
    data = JSON.parse(match[1]!);
  } catch {
    return null;
  }
  const result = parseAdsResult(data);
  if (!result) return null;
  return {
    before: reply.slice(0, match.index).trimEnd(),
    after: reply.slice(match.index + match[0].length).replace(/^(?:[ \t]*\r?\n)+/, ""),
    result,
  };
}

/** Last path segment, percent-decoded when that is valid. */
export function fileBasename(ref: string): string {
  const last = ref.split(/[\\/]/).pop() ?? "";
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
}

/**
 * Pair each ad with one of the reply's own attached image files by basename.
 * Only local image files count (never a web address), so the card cannot load
 * anything the reply did not attach. `used` holds the attached files the card
 * shows, which the reply's plain file list then leaves out.
 */
export function matchAdImages(ads: readonly AdsResultAd[], files: readonly string[]): {
  images: (string | undefined)[];
  used: Set<string>;
} {
  const byName = new Map<string, string>();
  const local = files.filter(ref => !/^https?:\/\//i.test(ref) && LOCAL_IMAGE_RE.test(ref));
  // An exact file name wins; then the name the reply attached, which the chat stores as
  // "<ms>-<index>-<random>-<name>" (27 Sep: the Cathy card matched none and showed no images).
  for (const ref of local) {
    const name = fileBasename(ref);
    if (name && !byName.has(name)) byName.set(name, ref);
  }
  for (const ref of local) {
    const name = fileBasename(ref).replace(STORED_PREFIX, "");
    if (name && !byName.has(name)) byName.set(name, ref);
  }
  const used = new Set<string>();
  const images = ads.map(ad => {
    const ref = ad.file ? byName.get(fileBasename(ad.file)) : undefined;
    if (ref) used.add(ref);
    return ref;
  });
  return { images, used };
}

/** An ad's own status decides; without one, being named in `launch` makes it a launch ad. */
export function adIsLaunch(ad: AdsResultAd, launchIds: ReadonlySet<string>): boolean {
  return ad.status ? ad.status === "launch" : launchIds.has(ad.id);
}

const COUNT_WORDS = ["", "one", "two", "three", "four", "five", "six"];
export function whyHeading(launchCount: number): string {
  if (launchCount === 1) return "Why this one";
  return `Why these ${COUNT_WORDS[launchCount] || launchCount}`;
}

export function helpersLine({ finished, failed }: { finished: number; failed: number }): string {
  // "All" only when nothing failed: "All 12 helpers finished, 1 failed" contradicts itself.
  const all = failed === 0 && finished > 1 ? "All " : "";
  const done = finished === 1 ? "1 helper finished" : finished === 0 ? "No helpers finished" : `${all}${finished} helpers finished`;
  return `${done}, ${failed} failed`;
}
